import React, { useState, useEffect, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { parseMonthToValue } from '../../utils/dataUtils'
import { MonthInfo, generateMonthListFromObjects, initializeSliderValues, getSliderIndicesFromValues, TMB_SLIDER_KEY, extractActualMonthValuesFromObjects } from '../../utils/tmbUtils'
import { exportToCSV } from '../../utils/tmbExportUtils'
import { useTmbSlider } from '../../context/TmbSliderContext'
import { isDatePostLaunch } from '../../utils/tmbLaunchDates'
import '../../styles/shared/index.css'
import './TeamBirthComments.css'

interface Comment {
    projectId: string
    recordId: string
    hospitalId: string
    surveyMonth: string
    comment: string
}

const HamburgerIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <g>
            <rect x="4" y="6" width="16" height="2" rx="1" />
            <rect x="4" y="11" width="16" height="2" rx="1" />
            <rect x="4" y="16" width="16" height="2" rx="1" />
        </g>
    </svg>
)

const TeamBirthComments: React.FC = () => {
    const { selectedHospitalIds, refreshKey } = useHospitalSelection()
    const { globalMonthList } = useTmbSlider()

    const [allComments, setAllComments] = useState<Comment[]>([])
    const [filteredComments, setFilteredComments] = useState<Comment[]>([])
    const [loading, setLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [monthList, setMonthList] = useState<MonthInfo[]>([])
    const [actualMonths, setActualMonths] = useState<Set<number>>(new Set())

    const persistenceKey = `${TMB_SLIDER_KEY}_${Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds}`
    const [sliderRange, setSliderRange] = usePersistedState<[number, number]>(persistenceKey, [0, 0])
    const [sliderStartIdx, sliderEndIdx] = getSliderIndicesFromValues(globalMonthList, sliderRange)
    const hasDataForRange = actualMonths.size === 0
        ? false
        : sliderRange[0] === 0 || Array.from(actualMonths).some(m => m >= sliderRange[0] && m <= sliderRange[1])

    const [menuOpen, setMenuOpen] = useState(false)

    const commentsContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!selectedHospitalIds || selectedHospitalIds.length === 0) {
            setAllComments([])
            setFilteredComments([])
            setLoading(false)
            setInitialLoading(false)
            setError(null)
            return
        }

        const hospitalIdsParam = Array.isArray(selectedHospitalIds)
            ? selectedHospitalIds.join(',')
            : selectedHospitalIds

        setLoading(true)
        setError(null)

        fetch(`/api/tmb/Teambirth_Comments/${hospitalIdsParam}`)
            .then(r => {
                if (!r.ok) throw new Error(`API error: ${r.status}`)
                return r.json()
            })
            .then(data => {
                const rows = data?.response?.result?.rows || []

                // column_order: ["project_id","record_id","hospital_id","survey_month","huddle_experience","survey_date"]
                const comments: Comment[] = rows
                    .filter((row: any[]) => isDatePostLaunch(row[2], row[5]))
                    .map((row: any[]) => ({
                        projectId: row[0],
                        recordId: row[1],
                        hospitalId: row[2],
                        surveyMonth: row[3],
                        comment: row[4]
                    }))
                    .filter((c: Comment) => c.comment && c.comment.trim() !== '')

                setAllComments(comments)

                if (comments.length === 0) {
                    setFilteredComments([])
                    setError(null)
                    setLoading(false)
                    setInitialLoading(false)
                    return
                }

                setActualMonths(extractActualMonthValuesFromObjects(comments, (c) => c.surveyMonth))
                const newMonthList = generateMonthListFromObjects(comments, (c) => c.surveyMonth)
                setMonthList(newMonthList)

                if (globalMonthList.length > 0) {
                    setSliderRange(prev => initializeSliderValues(globalMonthList, prev, 12))
                }

                setLoading(false)
                setInitialLoading(false)
            })
            .catch(err => {
                setError(err.message || 'Failed to load data')
                setLoading(false)
                setInitialLoading(false)
            })
    }, [selectedHospitalIds, refreshKey])

    useEffect(() => {
        if (globalMonthList.length === 0) return
        setSliderRange(prev => initializeSliderValues(globalMonthList, prev, 12))
    }, [globalMonthList])

    useEffect(() => {
        if (!allComments.length || !monthList.length) return

        const minMonthVal = sliderRange[0] || 0
        const maxMonthVal = sliderRange[1] || 0

        if (minMonthVal === 0 && maxMonthVal === 0) {
            setFilteredComments(allComments)
            return
        }

        const filtered = allComments.filter(comment => {
            const val = parseMonthToValue(comment.surveyMonth)
            return val >= minMonthVal && val <= maxMonthVal
        })

        setFilteredComments(filtered)
    }, [allComments, monthList, sliderRange])

    useEffect(() => {
        if (!commentsContainerRef.current || filteredComments.length === 0) return
        try {
            const savedScroll = sessionStorage.getItem('team_birth_comments_scroll')
            if (savedScroll) {
                commentsContainerRef.current.scrollTop = parseInt(savedScroll)
            }
        } catch (_) { }
    }, [filteredComments])

    useEffect(() => {
        const container = commentsContainerRef.current
        if (!container) return

        const handleScroll = () => {
            try {
                sessionStorage.setItem('team_birth_comments_scroll', String(container.scrollTop))
            } catch (_) { }
        }

        container.addEventListener('scroll', handleScroll)
        return () => container.removeEventListener('scroll', handleScroll)
    }, [])

    const handleSliderChange = (startIndex: number, endIndex: number) => {
        setSliderRange([globalMonthList[startIndex]?.value || 0, globalMonthList[endIndex]?.value || 0])
    }

    const handleDownloadCSV = () => {
        setMenuOpen(false)

        if (!filteredComments || filteredComments.length === 0) {
            alert('No comments to download. Please select a hospital and ensure data is loaded.')
            return
        }

        const startDate = globalMonthList[sliderStartIdx]?.label || ''
        const endDate = globalMonthList[sliderEndIdx]?.label || ''

        const headers = ['Hospital ID', 'Start Date', 'End Date', 'Comment']
        const sorted = [...filteredComments].sort((a, b) =>
            String(a.hospitalId).padStart(3, '0').localeCompare(String(b.hospitalId).padStart(3, '0'))
        )
        const rows = sorted.map(comment => [
            String(comment.hospitalId).padStart(3, '0'),
            startDate,
            endDate,
            comment.comment || ''
        ])

        const timestamp = new Date().toISOString().split('T')[0]
        exportToCSV({ headers, rows, filename: `Team_Birth_Comments_${timestamp}` })
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement
            if (!target.closest('.export-menu')) {
                setMenuOpen(false)
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [])

    if (initialLoading) {
        return (
            <div className="team-birth-comments-page tmb-page">
                <LoadingNotice title="Loading comments..." />
            </div>
        )
    }

    if (error) {
        return (
            <div className="team-birth-comments-page tmb-page">
                <div className="content-wrapper">
                    <div className="no-data-state">
                        <p>Error: {error}</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div id={generatePDFIds.dashboard()} className="team-birth-comments-page tmb-page">
            <div className="content-wrapper">
                <div className="header-wrapper">
                    <span className="page-title">TeamBirth Comments</span>

                    <div className="header-controls">
                        {globalMonthList.length > 0 && (
                            <DateRangeSlider
                                monthList={globalMonthList}
                                startIndex={sliderStartIdx}
                                endIndex={sliderEndIdx}
                                onChange={handleSliderChange}
                            />
                        )}
                    </div>
                </div>

                <div className="section-card" style={(!loading && filteredComments.length === 0 && allComments.length === 0) || !hasDataForRange ? { flex: 'none' } : undefined}>
                    {loading ? (
                        <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>Loading…</div>
                    ) : (filteredComments.length === 0 && allComments.length === 0) || !hasDataForRange ? (
                        <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>No data available</div>
                    ) : (<>
                        <div className="comments-header">
                            <div className="export-menu">
                                <button
                                    className="hamburger-btn"
                                    onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen) }}
                                >
                                    <HamburgerIcon />
                                </button>
                                <div className={`export-dropdown ${menuOpen ? 'show' : ''}`}>
                                    <div className="menu-item" onClick={handleDownloadCSV}>Download CSV</div>
                                </div>
                            </div>
                        </div>

                        <div className="comments-container" ref={commentsContainerRef}>
                            {filteredComments.length === 0 ? (
                                <div className="no-data-state">
                                    <span>No comments found for selected date range</span>
                                </div>
                            ) : (
                                filteredComments.map((comment, index) => (
                                    <div key={`${comment.recordId}-${index}`} className="comment-card">
                                        <div className="comment-text">"{comment.comment}"</div>
                                    </div>
                                ))
                            )}
                        </div>
                    </>)}
                </div>
            </div>
        </div>
    )
}

export default TeamBirthComments
